/* ============================================================
   cartAddView — add-to-cart failure fallback (01d4a604).
   Root cause of the OPS-19 stock-1 edge: AddItemAsync merges
   quantities server-side (dto.Quantity + existing line). Adding
   one more unit of a stock-1 product that is ALREADY in the
   cart fails validation and returns 400 isSuccess=false — the
   web client then shows a red error and never flips to
   «Перейти в корзину», even though the line sits in the cart
   (leftovers of a crashed run / an earlier visit). The right
   shopper next-step in that case is still the cart, so the
   product page resolves the failure against a live cart fetch.
   ============================================================ */

import type { Cart, CartItemView } from '../types/product';

/** Kind discriminator: numeric 0 / 'SellerProduct' / legacy undefined. */
function isSellerLine(line: CartItemView): boolean {
  const kind = line.kind;
  return kind === undefined || kind === null || kind === 0 || kind === '0' || kind === 'SellerProduct';
}

/** The seller-product line for this product, if one is in the cart.
 *  Quote-print lines (kind 1) never match — they carry their own
 *  performer/delivery snapshot and cannot be merged or "re-added". */
export function sellerLineForProduct(
  cart: Cart | null | undefined,
  productId: string,
): CartItemView | null {
  if (!cart?.items) return null;
  return cart.items.find((l) => l.productId === productId && isSellerLine(l)) ?? null;
}

export type CartAddFallback =
  | { kind: 'already-in-cart'; quantity: number; message: string }
  | { kind: 'error'; message: string };

/** Decide the product-page UI after a FAILED add: when the product is
 *  already in the cart the page flips to the «Перейти в корзину» state
 *  (with why the extra unit was refused); otherwise the plain error. */
export function addFailureFallback(
  errorMessage: string,
  line: CartItemView | null,
): CartAddFallback {
  if (line && line.quantity > 0) {
    return {
      kind: 'already-in-cart',
      quantity: line.quantity,
      message: `Уже в корзине — количество: ${line.quantity} шт.`,
    };
  }
  return { kind: 'error', message: errorMessage };
}
